/*
 * PKI.Csr — PKCS#10 certificate signing requests.
 *
 * A CSR is a self-signed statement "this subject owns this public key". The
 * self-signature is the only integrity check a CA can make before validation,
 * and it is the one thing no online CSR decoder bothers to verify — so a CSR
 * with a mangled key looks fine right up until the CA rejects it. We verify.
 *
 * SAN lives inside the extensionRequest attribute rather than at the top level,
 * which is why "I put the domains in and the CA only issued for the CN" is such
 * a common complaint: the generator wrote a CN and no extensionRequest at all.
 */
(function (root) {
    'use strict';

    var P = (root && root.PKI) || {};
    var Asn1 = P.Asn1 || (typeof require !== 'undefined' ? require('./asn1.js') : null);
    var Oid = P.Oid || (typeof require !== 'undefined' ? require('./oid.js') : null);
    var Keys = P.Keys || (typeof require !== 'undefined' ? require('./keys.js') : null);
    var X509 = P.X509 || (typeof require !== 'undefined' ? require('./x509.js') : null);

    var TAG = Asn1.TAG;
    var OID_EXTENSION_REQUEST = '1.2.840.113549.1.9.14';
    var OID_CHALLENGE_PASSWORD = '1.2.840.113549.1.9.7';

    function parse(der) {
        var bytes = Asn1.u8(der);
        var node = Asn1.parse(bytes);
        if (node.tag !== TAG.SEQUENCE || !node.children || node.children.length < 3) {
            throw new Error('CSR_NOT_SEQUENCE');
        }
        var cri = node.children[0];
        var sigAlgNode = node.children[1];
        var sigValue = node.children[2];
        var k = cri.children || [];
        if (k.length < 3) throw new Error('CSR_TRUNCATED');

        var subject = X509.parseName(k[1]);
        var spkiNode = k[2];

        var publicKey = null, publicKeyError = null;
        try { publicKey = Keys.parsePublic(spkiNode.full); }
        catch (e) { publicKeyError = e.message; }

        var attributes = [], extensions = [], challengePassword = null;
        if (k[3] && k[3].cls === 2 && k[3].tagNumber === 0) {
            (k[3].children || []).forEach(function (attr) {
                if (!attr.children || attr.children.length < 2) return;
                var type = attr.children[0].oid;
                var valueSet = attr.children[1];
                attributes.push({ oid: type, name: Oid.label(type), node: valueSet });

                if (type === OID_EXTENSION_REQUEST && valueSet.children && valueSet.children[0]) {
                    extensions = X509.parseExtensions(valueSet.children[0]);
                } else if (type === OID_CHALLENGE_PASSWORD && valueSet.children && valueSet.children[0]) {
                    challengePassword = valueSet.children[0].text || '';
                }
            });
        }

        var sigOid = sigAlgNode.children && sigAlgNode.children[0] ? sigAlgNode.children[0].oid : null;
        var sigInfo = Oid.sigInfo(sigOid);

        var csr = {
            der: bytes,
            version: k[0] ? k[0].bytes[0] : 0,
            subject: subject,
            publicKey: publicKey,
            publicKeyError: publicKeyError,
            spki: spkiNode.full,
            attributes: attributes,
            extensions: extensions,
            challengePassword: challengePassword,
            signatureAlgorithm: sigInfo.label,
            signatureAlgorithmOid: sigOid,
            signatureHash: sigInfo.hash,
            signatureKeyType: sigInfo.key,
            signatureValue: sigValue.bits || sigValue.bytes,
            criDer: cri.full
        };

        // Surface SAN the same way the certificate parser does, so consumers do
        // not have to know it arrived by a different route.
        var san = null;
        extensions.forEach(function (e) { if (e.oid === '2.5.29.17') san = e; });
        csr.sanEntries = san && Array.isArray(san.value) ? san.value : [];
        csr.dnsNames = csr.sanEntries.filter(function (e) { return e.kind === 'dns'; })
            .map(function (e) { return e.value; });
        csr.ipAddresses = csr.sanEntries.filter(function (e) { return e.kind === 'ip'; })
            .map(function (e) { return e.value; });

        var bc = null, ku = null, eku = null;
        extensions.forEach(function (e) {
            if (e.oid === '2.5.29.19') bc = e;
            if (e.oid === '2.5.29.15') ku = e;
            if (e.oid === '2.5.29.37') eku = e;
        });
        csr.requestedCa = !!(bc && bc.value && bc.value.ca);
        csr.keyUsage = ku && Array.isArray(ku.value) ? ku.value : [];
        csr.extKeyUsage = eku && Array.isArray(eku.value) ? eku.value : [];

        return csr;
    }

    /**
     * Check the CSR's own signature. Reuses the certificate verifier by handing
     * it the CSR's shape — the maths is identical, only the field names differ.
     */
    async function verify(csr) {
        return await X509.verifySignature({
            signatureHash: csr.signatureHash,
            signatureKeyType: csr.signatureKeyType,
            signatureAlgorithmOid: csr.signatureAlgorithmOid,
            signatureAlgorithm: csr.signatureAlgorithm,
            signatureValue: csr.signatureValue,
            tbsDer: csr.criDer
        }, csr.spki);
    }

    /**
     * Build and sign a CSR.
     * opts: {subject:[{short,value}], san:[{kind,value}], publicKeySpki,
     *        signerKey, signerKind, hash, keyUsage, extKeyUsage, challengePassword}
     */
    async function build(opts) {
        var subtle = Keys.subtle();
        var hash = opts.hash || 'SHA-256';
        var sigOid = opts.signerKind === 'Ed25519' ? '1.3.101.112' : Oid.sigOidFor(opts.signerKind, hash);
        if (!sigOid) throw new Error('no signature OID for ' + opts.signerKind + '/' + hash);

        var sigAlgId = opts.signerKind === 'RSA'
            ? Asn1.sequence(Asn1.oid(sigOid), Asn1.nullValue())
            : Asn1.sequence(Asn1.oid(sigOid));

        var attrs = [];
        var extSpec = {};
        if (opts.san && opts.san.length) extSpec.san = opts.san;
        if (opts.keyUsage && opts.keyUsage.length) extSpec.keyUsage = opts.keyUsage;
        if (opts.extKeyUsage && opts.extKeyUsage.length) extSpec.extKeyUsage = opts.extKeyUsage;
        if (opts.basicConstraints) extSpec.basicConstraints = opts.basicConstraints;

        var extNodes = X509.buildExtensions(extSpec);
        if (extNodes.length) {
            attrs.push(Asn1.sequence(
                Asn1.oid(OID_EXTENSION_REQUEST),
                Asn1.set(Asn1.sequence(extNodes))
            ));
        }
        if (opts.challengePassword) {
            attrs.push(Asn1.sequence(
                Asn1.oid(OID_CHALLENGE_PASSWORD),
                Asn1.set(Asn1.printableString(String(opts.challengePassword)))
            ));
        }

        var cri = Asn1.sequence(
            Asn1.integerFromNumber(0),
            X509.buildName(opts.subject),
            Asn1.u8(opts.publicKeySpki),
            // [0] IMPLICIT SET OF Attribute — present even when empty, which is
            // what openssl emits and what some CAs' parsers require.
            Asn1.context(0, Asn1.concat(attrs))
        );

        var signature = await X509.signBytes(subtle, opts.signerKey, opts.signerKind, hash, cri);
        return Asn1.sequence(cri, sigAlgId, Asn1.bitString(signature, 0));
    }

    var API = { parse: parse, build: build, verify: verify };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.Csr = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
